import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { SwitchInput } from "@/lib/Components/Form/Inputs/SwitchInput";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  createProDriverAgreementTemplate,
  updateProDriverAgreementTemplate,
} from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateMutations";
import { ProDriverAgreementTemplateRecord } from "@/app/ProDrivers/Agreements/GraphQL/agreementTemplateQueries";
import { AgreementContentForm } from "@/app/ProDrivers/Agreements/Components/AgreementContentForm";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { toast } from "sonner";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";

const templateValidation = z.object({
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Document title is required"),
  body: z.string().min(1, "Agreement wording is required"),
  is_default: z.boolean(),
});

type TemplateFormValues = z.infer<typeof templateValidation>;

export const AgreementTemplateDialog: DialogComponent<
  {
    template?: ProDriverAgreementTemplateRecord;
    initialValues?: { name?: string };
  },
  ProDriverAgreementTemplateRecord
> = ({ template, initialValues }) => {
  const { close, resolve } = useDialogState<ProDriverAgreementTemplateRecord>();
  const { mutateAsync: createTemplate } = useGqlMutation(
    createProDriverAgreementTemplate,
  );
  const { mutateAsync: updateTemplate } = useGqlMutation(
    updateProDriverAgreementTemplate,
  );

  return (
    <BaseDialog
      title={template ? "Edit agreement template" : "New agreement template"}
      subtitle="Insert drive details with the Insert detail menu — they are filled in when an agreement is created for a drive."
      fullScreen
      bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
    >
      <GenericForm<TemplateFormValues>
        className="flex min-h-0 flex-1 flex-col gap-4"
        initialValues={{
          name: template?.name ?? initialValues?.name ?? "",
          title: template?.title ?? "Contractor Agreement",
          body: template?.body ?? "",
          is_default: template?.is_default ?? false,
        }}
        validationSchema={toFormikValidationSchema(templateValidation)}
        onSubmit={async (values) => {
          if (template) {
            await updateTemplate({ id: template.id, input: values });
            toast.success("Template updated");
          } else {
            const res = await createTemplate({ input: values });
            toast.success("Template created");
            resolve?.(res.createProDriverAgreementTemplate);
          }

          await close();
        }}
      >
        <div className="grid shrink-0 grid-cols-1 gap-4 sm:grid-cols-3">
          <TextInput name="name" label="Template name" autoFocus />
          <TextInput name="title" label="Document title" />
          <SwitchInput
            name="is_default"
            label="Default agreement"
            tooltip="Sent automatically when a driver is assigned to a work order"
          />
        </div>
        <div className="flex min-h-0 flex-1 flex-col">
          <AgreementContentForm
            title={template?.title ?? "Contractor Agreement"}
            fill
            fullBleed={false}
          />
        </div>
        <div className="flex shrink-0 justify-end">
          <DialogSubmitButton text={template ? "Save template" : "Create"} />
        </div>
      </GenericForm>
    </BaseDialog>
  );
};
